import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Report, ReportElementUnion, GraphBuilderElement, CrosstabElement } from '../../types/report.types';
import type { AspectRatioPreset, GraphConfig } from '../../types/graph-builder.types';
import { MIDASProject } from '../../types/project.types';
import { DataSet, DatasetId } from '../../types/dataset.types';
import { ReportElementRenderer } from './ReportElementRenderer';
import { EditableTitle } from './EditableTitle';
import { ExportGraphModal, GraphExportFormat, GraphExportOptions } from './ExportGraphModal';
import { CrosstabEditModal } from './crosstab/CrosstabEditModal';
import { GraphBuilderEditModal } from './graph-builder/GraphBuilderEditModal';
import { ResizeElementDialog } from './ResizeElementDialog';
import { ReportMarkdownEditor } from './ReportMarkdownEditor';
import { extractSvgFromGraphElement } from '../utils/graphExportUtils';
import logger from '../utils/logger';
import { downloadSvgFile } from '../utils/webFileDownload';
import { useProjectStore } from '../stores/projectStore';
import { usePaneLayout } from '../stores/hooks/usePaneLayout';
import { createDataTableTab } from '../tab-system/TabRegistry';
import { saveProject } from '../services/storageService';
import * as styles from '../styles/components/report-view.css.ts';
import type { CrosstabConfiguration } from '../utils/crosstab/types';
import { REPORT_ELEMENT_SIZING } from '../constants/reportElementSizing';
import { parseReportContent } from '../utils/reportMarkdownParser';
import { getAspectRatioCssValue } from '../utils/aspectRatioUtils';


/**
 * ReportView - レポート表示コンポーネント
 * 
 * 機能:
 * - プロジェクトレベルでのレポート表示
 * - レポート要素の表示と管理
 * - プロジェクトに含まれるすべてのデータセットを参照可能
 */

interface ReportViewProps {
  project: MIDASProject | null;
  report: Report | null;
  onElementUpdate?: (elementId: string, updates: Record<string, unknown>) => void;
  onDeleteElement?: (elementId: string) => void;
  onReportNameChange?: (newName: string) => void;
  onContentChange?: (content: string) => void;
  onEditElement?: (elementId: string) => void;
  // 新しい要素追加用のハンドラー
  onAddElement?: (elementType: string, elementData?: Record<string, unknown>) => void;
  // 閲覧専用モード（埋め込み時など）- メニューを非表示にする
  readOnly?: boolean;
}

export function ReportView({
  project,
  report,
  onElementUpdate,
  onDeleteElement,
  onReportNameChange,
  onContentChange,
  onEditElement,
  onAddElement,
  readOnly = false
}: ReportViewProps) {
  // Markdown編集モード
  const [isEditing, setIsEditing] = useState(false);
  // ルーティング
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  // プロジェクトのデータセット辞書
  // Storeからすべてのデータセットを取得（派生データセットを含む）
  const getAllDatasets = useProjectStore(state => state.getAllDatasets);
  const allDatasets = getAllDatasets();
  const datasets = allDatasets.reduce((acc, dataset) => {
    acc[dataset.id] = dataset;
    return acc;
  }, {} as Record<string, DataSet>);

  // レイアウト操作フック
  const { addTab, getActiveContainer } = usePaneLayout();

  // グラフエクスポート状態
  const [isGraphExportModalOpen, setIsGraphExportModalOpen] = useState(false);
  const [exportingGraphElementId, setExportingGraphElementId] = useState<string | null>(null);

  // クロス集計編集状態
  const [isCrosstabEditModalOpen, setIsCrosstabEditModalOpen] = useState(false);
  const [editingCrosstabElementId, setEditingCrosstabElementId] = useState<string | null>(null);

  // グラフビルダー編集状態
  const [isGraphEditModalOpen, setIsGraphEditModalOpen] = useState(false);
  const [editingGraphElementId, setEditingGraphElementId] = useState<string | null>(null);

  // リサイズダイアログ状態
  const [isResizeDialogOpen, setIsResizeDialogOpen] = useState(false);
  const [resizingElementId, setResizingElementId] = useState<string | null>(null);

  // レポートメニュー状態
  const [isReportMenuOpen, setIsReportMenuOpen] = useState(false);

  // DOM要素への参照
  const reportHeaderRef = useRef<HTMLDivElement>(null);
  const reportContentRef = useRef<HTMLDivElement>(null);
  const reportMenuRef = useRef<HTMLDivElement>(null);

  // メニューの外側クリックで閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (reportMenuRef.current && !reportMenuRef.current.contains(event.target as Node)) {
        setIsReportMenuOpen(false);
      }
    }

    if (isReportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isReportMenuOpen]);

  // Markdown編集を開始
  const handleStartEditing = useCallback(() => {
    if (report) {
      setIsEditing(true);
      setIsReportMenuOpen(false);
    }
  }, [report]);

  // Markdown編集の内容変更（デバウンス済み）
  const handleContentChangeFromEditor = useCallback((newContent: string) => {
    if (onContentChange) {
      onContentChange(newContent);
    }
  }, [onContentChange]);

  // Markdown編集を終了
  const handleCloseEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  // 埋め込みモードに入るハンドラー
  const handleEnterEmbedMode = useCallback(async () => {
    if (!projectId || !report) return;
    setIsReportMenuOpen(false);

    // Embedモード遷移前にプロジェクトを保存
    // これにより、レイアウト状態（レポートタブを含む）がOPFSに永続化される
    const currentProject = useProjectStore.getState().project;
    if (currentProject) {
      await saveProject(currentProject);
      logger.info('[ReportView] Project saved before entering embed mode');
    }

    // URLパラメータを追加してナビゲート（履歴に追加される）
    navigate(`/project/${projectId}?embed=true&report-id=${report.id}`);
  }, [navigate, projectId, report]);

  // レポート名編集関連の関数（現在は未使用だが、将来の機能拡張のために残す）
  // const handleStartNameEdit = () => {
  //   if (report) {
  //     setEditingName(report.name);
  //     setIsEditingName(true);
  //   }
  // };

  // const handleCancelNameEdit = () => {
  //   setIsEditingName(false);
  //   setEditingName('');
  // };

  // const handleSaveNameEdit = () => {
  //   if (editingName.trim() && onReportNameChange) {
  //     onReportNameChange(editingName.trim());
  //   }
  //   setIsEditingName(false);
  //   setEditingName('');
  // };

  // const handleNameEditKeyDown = (e: React.KeyboardEvent) => {
  //   if (e.key === 'Enter') {
  //     handleSaveNameEdit();
  //   } else if (e.key === 'Escape') {
  //     handleCancelNameEdit();
  //   }
  // };




  // const handleSaveElementEdit = (elementId: string, updates: Record<string, unknown>) => {
  //   if (onElementUpdate) {
  //     onElementUpdate(elementId, updates);
  //   }
  // };

  // データセットを開くハンドラー
  const handleOpenSourceDataset = useCallback((elementId: string) => {
    if (!report) return;

    const element = report.elements[elementId];
    if (!element) return;

    // datasetIdを持つ要素かチェック
    const elementWithDataset = element as ReportElementUnion & { datasetId?: DatasetId };
    if (!elementWithDataset.datasetId) return;

    const datasetId = elementWithDataset.datasetId;
    const dataset = datasets[datasetId];
    if (!dataset) {
      logger.warn(`[ReportView] Dataset not found: ${datasetId}`);
      return;
    }

    // DataTableタブを作成して開く
    const dataTableTab = createDataTableTab(datasetId, dataset.name);
    const activeContainer = getActiveContainer();
    if (activeContainer) {
      addTab(activeContainer.id, dataTableTab, { activatePane: true });
      logger.info(`[ReportView] Opened dataset ${dataset.name} in DataTable tab`);
    }
  }, [report, datasets, addTab, getActiveContainer]);

  // 要素複製ハンドラー
  const handleDuplicateElement = useCallback((elementId: string) => {
    const element = report?.elements[elementId];
    if (element && onAddElement) {
      // 元の要素データをコピー（IDを除く）
      const elementData = { ...element } as Partial<ReportElementUnion>;
      delete (elementData as Record<string, unknown>).id;

      // 新しい要素として追加
      onAddElement(element.type, elementData);
    }
  }, [report, onAddElement]);

  // グラフエクスポートハンドラー
  const handleExportGraph = useCallback((elementId: string) => {
    setExportingGraphElementId(elementId);
    setIsGraphExportModalOpen(true);
    logger.info('[ReportView] Opening graph export modal for element:', elementId);
  }, []);

  // グラフエクスポート実行ハンドラー
  const handleExecuteGraphExport = useCallback(async (
    filePath: string,
    format: GraphExportFormat,
    options: GraphExportOptions
  ) => {
    if (!exportingGraphElementId || !report) {
      throw new Error('No graph element selected for export');
    }

    const element = report.elements[exportingGraphElementId];
    if (!element) {
      throw new Error('Graph element not found');
    }

    try {
      logger.info('[ReportView] Exporting graph:', {
        elementId: exportingGraphElementId,
        filePath,
        format,
        ...options
      });

      // SVGコンテンツを抽出
      // GraphBuilderElementにはtitleプロパティがある
      const graphElement = element as GraphBuilderElement;
      const svgContent = extractSvgFromGraphElement(
        exportingGraphElementId,
        graphElement.title || 'Graph',
        options
      );

      if (!svgContent) {
        throw new Error('Failed to extract SVG content from graph');
      }

      // Web版: ブラウザのダウンロード機能を使用
      const filename = filePath.split('/').pop() || 'graph.svg';
      downloadSvgFile(svgContent, filename);

      logger.info('[ReportView] Graph exported successfully', {
        filename,
        elementId: exportingGraphElementId,
        ...options
      });
    } catch (error) {
      logger.error('[ReportView] Graph export error:', error);
      throw error;
    }
  }, [exportingGraphElementId, report]);

  // 要素編集ハンドラー
  const handleEditElementInternal = useCallback((elementId: string) => {
    const element = report?.elements[elementId];
    if (element && element.type === 'crosstab') {
      setEditingCrosstabElementId(elementId);
      setIsCrosstabEditModalOpen(true);
      logger.info('[ReportView] Opening crosstab edit modal for element:', elementId);
    } else if (element && element.type === 'graph_builder') {
      setEditingGraphElementId(elementId);
      setIsGraphEditModalOpen(true);
      logger.info('[ReportView] Opening graph edit modal for element:', elementId);
    } else if (onEditElement) {
      // 他の要素タイプは親に委譲
      onEditElement(elementId);
    }
  }, [report, onEditElement]);

  // クロス集計設定保存ハンドラー
  const handleSaveCrosstabConfig = useCallback((newConfiguration: CrosstabConfiguration) => {
    if (!editingCrosstabElementId || !onElementUpdate) return;

    onElementUpdate(editingCrosstabElementId, {
      crosstabConfiguration: newConfiguration,
      lastModified: new Date().toISOString()
    });

    setIsCrosstabEditModalOpen(false);
    setEditingCrosstabElementId(null);
    logger.info('[ReportView] Crosstab configuration updated for element:', editingCrosstabElementId);
  }, [editingCrosstabElementId, onElementUpdate]);

  // グラフビルダー設定保存ハンドラー
  const handleSaveGraphConfig = useCallback((newConfig: GraphConfig, aspectRatio?: AspectRatioPreset) => {
    if (!editingGraphElementId || !onElementUpdate) return;

    const updates: Record<string, unknown> = {
      graphConfig: newConfig,
      lastModified: new Date().toISOString()
    };

    if (aspectRatio) {
      updates.aspectRatio = aspectRatio;
    }

    logger.info('[ReportView] Saving graph config:', { elementId: editingGraphElementId, newConfig });
    onElementUpdate(editingGraphElementId, updates);

    setIsGraphEditModalOpen(false);
    setEditingGraphElementId(null);
    logger.info('[ReportView] Graph configuration updated for element:', editingGraphElementId);
  }, [editingGraphElementId, onElementUpdate]);

  // デバッグ: reportの変更を監視
  useEffect(() => {
    if (report) {
      const elementIds = Object.keys(report.elements);
      if (elementIds.length > 0) {
        const firstElement = report.elements[elementIds[0]];
        if (firstElement.type === 'graph_builder') {
          logger.info('[ReportView] Current report graphConfig:', (firstElement as GraphBuilderElement).graphConfig);
        }
      }
    }
  }, [report]);

  // リサイズダイアログを開くハンドラー
  const handleResizeElement = useCallback((elementId: string) => {
    setResizingElementId(elementId);
    setIsResizeDialogOpen(true);
    logger.info('[ReportView] Opening resize dialog for element:', elementId);
  }, []);

  // リサイズ保存ハンドラー
  const handleResizeSave = useCallback((width: 'fit' | number, height: number, aspectRatio: AspectRatioPreset) => {
    if (!resizingElementId || !onElementUpdate) return;

    onElementUpdate(resizingElementId, {
      width,
      height,
      aspectRatio,
      lastModified: new Date().toISOString()
    });

    setIsResizeDialogOpen(false);
    setResizingElementId(null);
    logger.info('[ReportView] Element resized:', { elementId: resizingElementId, width, height, aspectRatio });
  }, [resizingElementId, onElementUpdate]);


  if (!project) {
    return (
      <div className={styles.reportView}>
        <div className={styles.reportEmptyState}>
          <p>Load a project to display reports</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className={styles.reportView}>
        <div className={styles.reportLoading}>
          <p>Choose a report from the dropdown above or create a new one.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.reportView} ${styles.tabContentScrollable}`} data-testid="report-content">
      {/* レポートヘッダー */}
      <div className={styles.reportHeader} ref={reportHeaderRef}>
        <div className={styles.reportHeaderContent}>
          <div className={styles.reportTitleSection}>
            {/* EditableTitleコンポーネントでタイトルを直接編集可能にした */}
            <div className={styles.reportTitleDisplay}>
                <div className={styles.reportTitleLeft}>
                  <EditableTitle
                    title={report.name}
                    onTitleChange={onReportNameChange || (() => {})}
                    placeholder="Click to add report title..."
                    className={styles.reportTitle}
                  />
                </div>
                
                {/* レポートメニューボタン（readOnlyモードでは非表示） */}
                {!readOnly && projectId && (
                  <div className={`${styles.reportMenuContainer} no-export`} ref={reportMenuRef}>
                    <button
                      className={styles.reportMenuButton}
                      onClick={() => setIsReportMenuOpen(!isReportMenuOpen)}
                      title="Report options"
                    >
                      ⋮
                    </button>

                    {isReportMenuOpen && (
                      <div className={styles.reportMenuDropdown}>
                        <button
                          className={styles.reportMenuItem}
                          onClick={handleStartEditing}
                          title="Edit report content as Markdown"
                          data-testid="edit-content-button"
                        >
                          Edit Content
                        </button>
                        <div className={styles.reportMenuDivider} />
                        <button
                          className={styles.reportMenuItem}
                          onClick={handleEnterEmbedMode}
                          title="Open report in embed mode for viewing or printing"
                          data-testid="enter-embed-mode-button"
                        >
                          Embed Mode
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
          </div>
          
        </div>
      </div>
      
      {/* レポートコンテンツ */}
      <div
        className={styles.reportContent}
        ref={reportContentRef}
        style={{
          backgroundColor: report.reportSettings.backgroundColor,
          padding: '16px',
          position: 'relative'
        }}
      >
        {isEditing ? (
          // Markdown編集モード（CodeMirrorベース）
          <ReportMarkdownEditor
            content={report.content || ''}
            elements={report.elements}
            datasets={datasets}
            onChange={handleContentChangeFromEditor}
            onClose={handleCloseEditing}
            onDeleteElement={onDeleteElement}
            onEditElement={handleEditElementInternal}
            onDuplicateElement={handleDuplicateElement}
            onOpenSourceDataset={handleOpenSourceDataset}
            onExportElement={handleExportGraph}
            onResizeElement={handleResizeElement}
          />
        ) : (
          // 表示モード
          <ReportContentRenderer
            content={report.content}
            elements={report.elements}
            datasets={datasets}
            models={project?.models}
            defaultElementMargin={report.reportSettings.defaultElementMargin}
            onElementUpdate={onElementUpdate}
          />
        )}
      </div>

      {/* グラフエクスポートモーダル */}
      <ExportGraphModal
        isOpen={isGraphExportModalOpen}
        onClose={() => {
          setIsGraphExportModalOpen(false);
          setExportingGraphElementId(null);
        }}
        onExport={handleExecuteGraphExport}
        graphTitle={
          exportingGraphElementId && report
            ? (report.elements[exportingGraphElementId] as GraphBuilderElement | undefined)?.title
            : undefined
        }
      />

      {/* クロス集計編集モーダル */}
      {editingCrosstabElementId && report && (
        <CrosstabEditModal
          isOpen={isCrosstabEditModalOpen}
          element={report.elements[editingCrosstabElementId] as CrosstabElement}
          dataset={datasets[(report.elements[editingCrosstabElementId] as CrosstabElement)?.datasetId]}
          onSave={handleSaveCrosstabConfig}
          onClose={() => {
            setIsCrosstabEditModalOpen(false);
            setEditingCrosstabElementId(null);
          }}
        />
      )}

      {/* グラフビルダー編集モーダル */}
      {editingGraphElementId && report && (
        <GraphBuilderEditModal
          isOpen={isGraphEditModalOpen}
          element={report.elements[editingGraphElementId] as GraphBuilderElement}
          dataset={datasets[(report.elements[editingGraphElementId] as GraphBuilderElement)?.datasetId]}
          onSave={handleSaveGraphConfig}
          onClose={() => {
            setIsGraphEditModalOpen(false);
            setEditingGraphElementId(null);
          }}
        />
      )}

      {/* Resize Element Dialog */}
      {isResizeDialogOpen && resizingElementId && (
        <ResizeElementDialog
          element={report.elements[resizingElementId]!}
          onResize={handleResizeSave}
          onClose={() => {
            setIsResizeDialogOpen(false);
            setResizingElementId(null);
          }}
        />
      )}
    </div>
  );
}

// Markdownコンテンツと要素を統合してレンダリングするコンポーネント
// 要素の操作（削除・編集など）は編集モードの「Report Elements」セクションで行う
interface ReportContentRendererProps {
  content: string;
  elements: Record<string, ReportElementUnion>;
  datasets: Record<string, DataSet>;
  models?: Record<string, import('../../types/model.types').TrainedModel>;
  defaultElementMargin: number;
  onElementUpdate?: (elementId: string, updates: Record<string, unknown>) => void;
}

function ReportContentRenderer({
  content,
  elements,
  datasets,
  models,
  defaultElementMargin,
  onElementUpdate
}: ReportContentRendererProps) {
  // Markdown本文をトークンにパース
  const tokens = useMemo(() => parseReportContent(content || ''), [content]);

  // 空のレポート
  const isEmpty = !content && Object.keys(elements).length === 0;

  if (isEmpty) {
    return (
      <div className={styles.emptyReport}>
        <div className={styles.emptyReportIcon}>Page</div>
        <h3 className={styles.emptyReportTitle}>Report is empty</h3>
        <p className={styles.emptyReportMessage}>
          Add elements to start creating your report
        </p>
      </div>
    );
  }

  return (
    <div className={styles.reportMarkdownContent}>
      {tokens.map((token, index) => {
        if (token.type === 'text') {
          // Markdownテキストをレンダリング
          return (
            <div key={`text-${index}`} className={styles.markdownSection}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {token.content}
              </ReactMarkdown>
            </div>
          );
        } else {
          // 要素参照をレンダリング
          const element = elements[token.elementId];
          if (!element) {
            // 要素が見つからない場合はプレースホルダーを表示
            return (
              <div key={`missing-${index}`} className={styles.missingElement}>
                <p>Element not found: {token.elementId}</p>
              </div>
            );
          }

          const width = element.width ?? REPORT_ELEMENT_SIZING.DEFAULT_WIDTH;
          const height = element.height ?? REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT;
          // 既存要素（aspectRatio未設定）は 'custom' として扱い、固定高さを使用
          const aspectRatio = element.aspectRatio ?? 'custom';
          const aspectRatioCss = getAspectRatioCssValue(aspectRatio);

          return (
            <div
              key={`element-${index}`}
              className={styles.reportElement}
              style={{
                marginBottom: `${defaultElementMargin}px`,
                position: 'relative',
                width: width === 'fit' ? '100%' : `${width}px`,
                // アスペクト比が設定されている場合はCSSで高さを自動計算
                // 'custom' の場合は固定高さを使用
                aspectRatio: aspectRatioCss,
                height: aspectRatio === 'custom' ? `${height}px` : undefined
              }}
              data-testid={`report-element-${element.type}`}
              data-element-type={element.type}
              data-element-id={element.id}
            >
              {/* レポート要素メニューは編集モードの「Report Elements」セクションに移動 */}

              <ReportElementRenderer
                element={element}
                datasets={datasets}
                models={models}
                onElementUpdate={onElementUpdate}
                width={width}
                height={aspectRatio === 'custom' ? height : undefined}
              />
            </div>
          );
        }
      })}
    </div>
  );
}
